import { Box } from '@mui/material';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { IS_SA } from '../../../../../config/appConfig';
import { AppRoutes } from '../../../../../constants/appRoutes';
import { GTMEvents } from '../../../../../constants/gtmEvents';
import { InvestorChannels } from '../../../../../constants/investorChannels';
import { OTPTypes } from '../../../../../constants/otpTypes';
import { buildLocalizeRoute } from '../../../../../helpers/languageHelper';
import { recordInvestorSignupStarted } from '../../../../../helpers/openPanelHelper';
import { recordInvestorSignupStarted as recordInvestorSignupStartedCustomerIo } from '../../../../../helpers/customerIoHelper';
import { pushToDataLayer, saveContactNumber } from '../../../../../helpers/pushToGTMHelper';
import { getUser, getUserFromToken, Logout, loginUsingToken } from '../../../../../services/authService';
import { UserService } from '../../../../../services/userService';
import { Toaster } from '../../../../../shared/components/common/toaster';
import { OTPAuthenticationDialog } from '../../../../../shared/components/otpAuthenticationDialog';
import { BaseRegistration } from '../../../core/baseRegistration';
import { SignupPageWrapper } from '../../../core/signUpPageWrapper';

// GTM wants a space between dialling code and number, OpenPanel wants none —
// that separator is the only difference, so both callers share one formatter.
const formatMobileNumber = (contactNumber, separator = '') =>
	contactNumber?.code && contactNumber?.number
		? `+${contactNumber.code}${separator}${contactNumber.number}`
		: undefined;

export const InvestorBaseRegistration = ({ isKsaInternationalInvestor = false }) => {
	const userService = new UserService();
	const navigate = useNavigate();
	const location = useLocation();
	const { t } = useTranslation();
	const [loading, setLoading] = useState(false);
	const [showOTPAuth, setShowOTPAuth] = useState(false);
    const enteredInternationalFlow = !!isKsaInternationalInvestor || !!location?.state?.isKsaInternationalInvestor;
	const [persistedInternationalFlow, setPersistedInternationalFlow] = useState(
		localStorage.getItem("isKsaInternationalInvestor") === '1'
	);
    const isKsaInternationalFlow = IS_SA && (enteredInternationalFlow || persistedInternationalFlow);

    useEffect(() => {
        if (!IS_SA) return;
        if (enteredInternationalFlow) {
			if (!persistedInternationalFlow) {
				localStorage.setItem("isKsaInternationalInvestor", '1');
				setPersistedInternationalFlow(true);
			}
            return;
        }

		if (persistedInternationalFlow) {
			localStorage.removeItem("isKsaInternationalInvestor");
			setPersistedInternationalFlow(false);
		}
    }, [enteredInternationalFlow, persistedInternationalFlow]);
	const onSubmit = (values) => {
		const data = {
			...values,
			referral: localStorage.getItem('ref'),
			channel: InvestorChannels.Core,
			isKsaInternationalInvestor: isKsaInternationalFlow
		};
		if (!data.termsConditionsForInvestmentsAccepted) {
			Toaster({ msg: t('termsAndConditionsRequired') });
			return;
		}

		setLoading(true);
		userService
			.registerInvestor(data)
			.then((resp) => {
				let tokenModel = resp.data;
				if (tokenModel) {
					setLoading(false);
					localStorage.removeItem('ref');
					const email = data.email;
					const phone = formatMobileNumber(data.contactNumber, ' ');
					const mobileNumber = formatMobileNumber(data.contactNumber);
					saveContactNumber(phone);
					pushToDataLayer(GTMEvents.signUpSubmit, {
						userInfo: {
							email: email,
							phone: phone
						}
					});
					if (!resp.data?.emailConfirmed) {
						// The backend issues no token until the email is confirmed: this response
						// carries `token: null` and the Identity GUID as `userId`. Fall back to it,
						// otherwise the verify-email majority — the cohort this path exists to
						// rescue — stays anonymous. The fallback user has no role claim, so
						// `user_type` is legitimately absent from identify on this path.
						const signupUser =
							getUserFromToken(tokenModel.token) ||
							(tokenModel.userId ? { nameid: tokenModel.userId, email } : null);
						recordInvestorSignupStarted(signupUser, { mobileNumber });
						recordInvestorSignupStartedCustomerIo(signupUser, { mobileNumber });
						navigate(buildLocalizeRoute(`${AppRoutes.VerifyEmail}?e=${encodeURIComponent(data.email)}&type=2`));
					} else {
						localStorage.removeItem("isKsaInternationalInvestor");
						setPersistedInternationalFlow(false);
						loginUsingToken(tokenModel.token);
						recordInvestorSignupStarted(getUser(), { mobileNumber });
						recordInvestorSignupStartedCustomerIo(getUser(), { mobileNumber });
						if (tokenModel.otpRequested) {
							setShowOTPAuth(true);
						} else {
							onNavigateNextStep();
						}
					}
				}
			})
			.catch((err) => {
				setLoading(false);
			});
	};

	const onNavigateNextStep = () => {
		const nextStep = IS_SA && !isKsaInternationalFlow ? AppRoutes.IRegNafathVerification : AppRoutes.IRegPersonalInfo;
		const route = `${AppRoutes.InvestorRegistration}/${nextStep}`;
		navigate(buildLocalizeRoute(route), { state: { isKsaInternationalInvestor: isKsaInternationalFlow } });
	};

	return (
		<Box className="flex flex-col h-full">
			<SignupPageWrapper>
				<BaseRegistration
					loading={loading}
					clientType={'investor'}
					isSA={IS_SA && !isKsaInternationalFlow}
					onSubmitForm={(values) => {
						onSubmit(values);
					}}></BaseRegistration>
			</SignupPageWrapper>
			{showOTPAuth ? (
				<OTPAuthenticationDialog
					type={OTPTypes.Authentication}
					onClose={(isVerified) => {
						setShowOTPAuth(false);
						if (isVerified) {
							onNavigateNextStep();
						} else {
							Logout();
							navigate(buildLocalizeRoute(AppRoutes.Login));
						}
					}}
					state={showOTPAuth}></OTPAuthenticationDialog>
			) : null}
		</Box>
	);
};
